import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { CanonFact, CanonCandidate, CanonCategory } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useModelPicker } from "../ai/ModelPicker.js";

interface Props {
  projectId: string;
  onChanged?: () => void;
}

const CATEGORIES: CanonCategory[] = ["character", "world", "timeline", "relationship", "other"];
const CAT_LABEL: Record<string, string> = { character: "Characters", world: "World", timeline: "Timeline", relationship: "Relationships", other: "Other" };

/**
 * Canon Locks — author-declared authoritative facts (with optional temporal
 * scope). Locked canon outranks AI inference everywhere (Ask the Book, context).
 * "Check canon" scans the manuscript for contradictions → Needs Review.
 */
export function CanonPanel({ projectId, onChanged }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [facts, setFacts] = useState<CanonFact[]>([]);
  const [suggestions, setSuggestions] = useState<CanonCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [subject, setSubject] = useState("");
  const [fact, setFact] = useState("");
  const [scope, setScope] = useState("");
  const [category, setCategory] = useState<CanonCategory>("character");
  const [scenes, setScenes] = useState<{ id: string; label: string }[]>([]);
  const [effFrom, setEffFrom] = useState("");
  const [effUntil, setEffUntil] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.canon(projectId); setFacts(r.facts); setSuggestions(r.suggestions); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't load canon"); }
    finally { setLoading(false); }
  }, [projectId, toast]);
  useEffect(() => { void load(); }, [load]);

  // Scene options for temporal scope (ordered by chapter then scene).
  useEffect(() => {
    api.manuscript(projectId).then((m) => {
      const chPos = new Map(m.chapters.map((c) => [c.id, c.position] as const));
      const chTitle = new Map(m.chapters.map((c) => [c.id, c.title] as const));
      const ordered = [...m.scenes]
        .sort((a, b) => (chPos.get(a.chapterId) ?? 0) - (chPos.get(b.chapterId) ?? 0) || a.position - b.position)
        .map((s) => ({ id: s.id, label: `${chTitle.get(s.chapterId) ?? ""} · ${s.title}` }));
      setScenes(ordered);
    }).catch(() => undefined);
  }, [projectId]);
  const sceneLabel = (id: string | null) => scenes.find((s) => s.id === id)?.label ?? null;

  const add = async (body: { fact: string; subject?: string | null; scopeLabel?: string | null; category?: CanonCategory; effectiveFromSceneId?: string | null; effectiveUntilSceneId?: string | null }) => {
    if (!body.fact.trim()) return;
    try { await api.createCanon(projectId, body); await load(); onChanged?.(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't add"); }
  };
  const addManual = async () => {
    await add({ fact: fact.trim(), subject: subject.trim() || null, scopeLabel: scope.trim() || null, category, effectiveFromSceneId: effFrom || null, effectiveUntilSceneId: effUntil || null });
    setFact(""); setSubject(""); setScope(""); setEffFrom(""); setEffUntil("");
  };
  const toggleLock = async (f: CanonFact) => {
    try { await api.updateCanon(projectId, f.id, { locked: f.locked ? 0 : 1 }); await load(); onChanged?.(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Update failed"); }
  };
  const del = async (f: CanonFact) => {
    try { await api.deleteCanon(projectId, f.id); setFacts((p) => p.filter((x) => x.id !== f.id)); onChanged?.(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Delete failed"); }
  };
  const check = async () => {
    const choice = await pickModel({ task: "continuity" });
    if (!choice) return;
    setChecking(true);
    try {
      const r = await api.checkCanon(projectId, choice);
      toast.success(r.result.conflicts > 0 ? `Found ${r.result.conflicts} possible canon conflict${r.result.conflicts === 1 ? "" : "s"} — see Needs Review.` : "No canon conflicts found.");
      onChanged?.();
    } catch (err) { toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Check failed"); }
    finally { setChecking(false); }
  };

  if (loading) return <div className="insights-loading"><Icon name="sparkles" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>;

  const byCat = (c: CanonCategory) => facts.filter((f) => f.category === c);

  return (
    <div className="intel-scroll">
      <section className="card insight-card">
        <div className="insight-card-head">
          <Icon name="vault" size={16} style={{ color: "var(--accent)" }} />
          <h3>Canon Locks</h3>
          <span className="spacer" />
          <button className="btn btn-sm btn-ghost" onClick={() => void check()} disabled={checking || facts.filter((f) => f.locked).length === 0}>
            <Icon name={checking ? "clock" : "sparkles"} size={13} className={checking ? "spin" : ""} /> {checking ? "Checking…" : "Check canon (AI)"}
          </button>
        </div>
        <p className="faint" style={{ fontSize: 12, margin: "0 0 12px" }}>
          Facts you lock here are authoritative — they outrank AI inference in Ask the Book and context, and "Check canon" flags scenes that contradict them.
        </p>
        <div className="canon-add">
          <select className="select" value={category} onChange={(e) => setCategory(e.target.value as CanonCategory)} style={{ maxWidth: 130 }}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
          </select>
          <input className="input" placeholder="Subject (e.g. Tyler)" value={subject} onChange={(e) => setSubject(e.target.value)} style={{ maxWidth: 160 }} />
          <input className="input" placeholder="Fact (e.g. is 25)" value={fact} onChange={(e) => setFact(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void addManual(); }} />
          <input className="input" placeholder="Scope label (optional)" value={scope} onChange={(e) => setScope(e.target.value)} style={{ maxWidth: 150 }} />
          <button className="btn btn-sm btn-primary" onClick={() => void addManual()} disabled={!fact.trim()}>Lock</button>
        </div>
        {scenes.length > 0 && (
          <div className="canon-add" style={{ marginTop: 8 }}>
            <span className="faint" style={{ fontSize: 11, alignSelf: "center" }}>True from</span>
            <select className="select" value={effFrom} onChange={(e) => setEffFrom(e.target.value)} style={{ maxWidth: 220 }}>
              <option value="">always (immutable)</option>
              {scenes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <span className="faint" style={{ fontSize: 11, alignSelf: "center" }}>until</span>
            <select className="select" value={effUntil} onChange={(e) => setEffUntil(e.target.value)} style={{ maxWidth: 220 }}>
              <option value="">end of book</option>
              {scenes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        )}

        {suggestions.length > 0 && (
          <div className="canon-suggest">
            <span className="faint" style={{ fontSize: 11 }}>Suggested from your entities:</span>
            {suggestions.slice(0, 10).map((s, i) => (
              <button key={i} className="chip canon-suggestion" onClick={() => void add({ fact: s.fact, subject: s.subject, category: s.category })}>
                + {s.subject} {s.fact}
              </button>
            ))}
          </div>
        )}
      </section>

      {facts.length === 0 ? (
        <section className="card insight-card"><p className="muted insight-empty">No canon locked yet. Lock a fact above, or add one from the suggestions.</p></section>
      ) : (
        CATEGORIES.filter((c) => byCat(c).length > 0).map((c) => (
          <section key={c} className="card insight-card">
            <div className="insight-card-head"><Icon name="book" size={15} style={{ color: "var(--accent)" }} /><h3>{CAT_LABEL[c]}</h3><span className="chip">{byCat(c).length}</span></div>
            <ul className="canon-list">
              {byCat(c).map((f) => (
                <li key={f.id} className={`canon-item ${f.locked ? "is-locked" : ""}`}>
                  <button className="canon-lock" title={f.locked ? "Locked (click to unlock)" : "Unlocked (click to lock)"} onClick={() => void toggleLock(f)}>
                    {f.locked ? "🔒" : "🔓"}
                  </button>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <span className="canon-fact">{f.subject && <span className="strong">{f.subject}</span>} {f.fact}</span>
                    {f.scopeLabel && <span className="chip canon-scope">{f.scopeLabel}</span>}
                    {f.effectiveFromSceneId && <span className="chip canon-scope">from {sceneLabel(f.effectiveFromSceneId) ?? "scene"}</span>}
                    {f.effectiveUntilSceneId && <span className="chip canon-scope">until {sceneLabel(f.effectiveUntilSceneId) ?? "scene"}</span>}
                    {f.origin === "ai" && <span className="chip" style={{ fontSize: 10 }}>AI</span>}
                  </div>
                  <button className="btn btn-icon btn-ghost" title="Delete" onClick={() => void del(f)}><Icon name="trash" size={13} /></button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
